import { Namespace } from '@36node-fcp/auth-sdk';
import { Subject } from '@36node-fcp/core-sdk';
import { Spin } from 'antd';
import { concat } from 'lodash';
import { useMemo } from 'react';

import { AdColumnsType, AdTable } from 'src/components/antd/ad-table';
import { ExportModal } from 'src/components/xlsx-export-modal';
import { ROOT_NS } from 'src/config';
import { NamespaceReportItem, useNamespaceReport } from 'src/features/report';
import { useNamespaceList } from 'src/features/users';
import { getNsPathnameFromString } from 'src/lib/other';
import { useSlice } from 'src/lib/react-slice';

import { useReportSearch } from './report.context';
import SearchForm from './search.form';

type State = {
  exportVisible: boolean;
};

const initState: State = {
  exportVisible: false,
};
const reducers = {
  openExport(state: State) {
    state.exportVisible = true;
  },
  closeExport(state: State) {
    state.exportVisible = false;
  },
};

const columnsPart1 = [
  {
    title: '过车数',
    dataIndex: 'trackCount',
  },
];

const columnsPart2 = [
  {
    title: '违法数',
    dataIndex: 'illegalCount',
  },
  {
    title: '预警数',
    dataIndex: 'warningCount',
  },
  {
    title: '查处数',
    dataIndex: 'warningCloseCount',
  },
];

/**
 * 部门报表
 */
const NamespaceReport: React.FC = () => {
  const [{ exportVisible }, dispatch] = useSlice(reducers, initState);
  const [search] = useReportSearch();
  const [{ loading, list, result, total }] = useNamespaceReport(search);
  const [{ result: namespaces = [], loading: nsLoading }] = useNamespaceList({ ns_start: ROOT_NS, _limit: 1000 });

  const columns = useMemo<AdColumnsType<NamespaceReportItem>>(() => {
    const nsColumn = {
      title: '部门名',
      dataIndex: 'ns',
      compute: (val: Namespace) => val.name,
    };
    return search.subject.includes(Subject.VEHICLE)
      ? concat([nsColumn], columnsPart1, columnsPart2)
      : concat([nsColumn], columnsPart2);
  }, [search]);

  const exportColumns = useMemo<AdColumnsType<NamespaceReportItem>>(() => {
    const exportNsColumn = {
      title: '部门名',
      dataIndex: 'ns',
      compute: (val: Namespace) => getNsPathnameFromString(val.ns, namespaces, 1),
    };
    return search.subject.includes(Subject.VEHICLE)
      ? concat([exportNsColumn], columnsPart1, columnsPart2)
      : concat([exportNsColumn], columnsPart2);
  }, [search, nsLoading]);

  if (loading || nsLoading) {
    // TODO: 去掉 Loading 会导致表格树状结构无法展开
    return <Spin />;
  }

  return (
    <>
      <AdTable
        bordered
        columns={columns}
        title={<div>按部门（{total}）</div>}
        rowKey="uniqKey"
        loading={loading}
        scroll={{ x: 'max-content' }}
        dataSource={result}
        showColumnsFilter={false}
        pagination={false}
        onExport={dispatch.openExport}
        extraTools={<SearchForm />}
        expandable={{ defaultExpandAllRows: true }}
      />
      {exportVisible && (
        <ExportModal
          dataSource={list}
          columns={exportColumns}
          filename="部门报表.xlsx"
          onClose={dispatch.closeExport}
          title="部门报表"
          total={total}
        />
      )}
    </>
  );
};

export default NamespaceReport;
